import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { User, Mail, LogOut } from 'lucide-react';
import { supabase } from '../config/supabase';
import { useNavigate } from 'react-router-dom';
import NavBar from '../components/NavBar';

const Profile = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProfile();
  }, [user]);

  const fetchProfile = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      setProfile(data);
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    const { error } = await signOut();
    if (!error) {
      navigate('/login');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950">
        <div className="fixed inset-0 bg-gradient-to-br from-violet-950 via-purple-900 to-gray-950">
          <div className="flex items-center justify-center min-h-screen">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 overflow-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 bg-gradient-to-br from-violet-950 via-purple-900 to-gray-950">
        <div className="absolute inset-0">
          {/* Floating database icons */}
          <div className="absolute top-20 left-10 animate-float">
            <div className="w-20 h-20 bg-violet-600/20 rounded-lg backdrop-blur-sm flex items-center justify-center text-3xl">
              🗄️
            </div>
          </div>
          <div className="absolute top-40 right-20 animate-float-slow">
            <div className="w-16 h-16 bg-purple-600/20 rounded-lg backdrop-blur-sm flex items-center justify-center text-2xl">
              💾
            </div>
          </div>
          <div className="absolute bottom-40 left-1/4 animate-float-slower">
            <div className="w-24 h-24 bg-violet-700/20 rounded-lg backdrop-blur-sm flex items-center justify-center text-4xl">
              🗃️
            </div>
          </div>
          <div className="absolute top-1/3 right-1/3 animate-pulse-slow">
            <div className="w-32 h-32 bg-gradient-radial from-violet-600/30 to-transparent rounded-full blur-xl"></div>
          </div>
          <div className="absolute bottom-1/4 right-1/4 animate-pulse-slow" style={{ animationDelay: '2s' }}>
            <div className="w-40 h-40 bg-gradient-radial from-purple-600/30 to-transparent rounded-full blur-xl"></div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <NavBar />

      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          <div className="card-gradient p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="bg-gradient-to-r from-violet-600 to-purple-600 px-4 py-3 rounded-lg mb-6">
                <h3 className="text-lg font-bold text-white">
                  User Profile
                </h3>
              </div>
            </div>
            
            {/* Profile Content */}
            <div className="flex flex-col items-center">
              {user?.user_metadata?.avatar_url ? (
                <img
                  className="h-24 w-24 rounded-full border-4 border-violet-600/50"
                  src={user.user_metadata.avatar_url}
                  alt={user.user_metadata.full_name || 'User avatar'}
                />
              ) : (
                <div className="h-24 w-24 rounded-full bg-gradient-to-r from-violet-600 to-purple-600 flex items-center justify-center">
                  <User className="h-12 w-12 text-white" />
                </div>
              )}
              
              <div className="mt-6 text-center">
                <h4 className="text-2xl font-bold text-white mb-2">
                  {user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'User'}
                </h4>
                
                <div className="flex items-center justify-center text-gray-300 mb-6">
                  <Mail className="h-4 w-4 mr-2" />
                  <span className="text-sm">{user?.email}</span>
                </div>
              </div>
              
              {/* Sign Out Button */}
              <button
                onClick={handleSignOut}
                className="w-full flex justify-center items-center px-4 py-3 border border-transparent text-sm font-medium rounded-md text-white bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all duration-200 transform hover:scale-[1.02]"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;